import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { X, Mail, Clock, MessageSquare, Edit3, Save, ChevronLeft, User, Tag, Calendar, AlertCircle, CheckCircle2, UserCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { cn, sendStatusUpdateEmail } from '../../lib/utils';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { useToast } from '../../components/ui/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { format } from 'date-fns';

const FeedbackDetailView = ({ feedback, onClose = () => { }, onStatusUpdate = () => { }, isMobile }) => {
  const { toast } = useToast();
  const [internalNote, setInternalNote] = useState(feedback?.internalNote || '');
  const [isEditingNote, setIsEditingNote] = useState(false);

  const userData = feedback?.userData || {};
  const name = userData.displayName || userData.email?.split('@')[0] || 'User';
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const date = feedback?.date ? format(new Date(feedback.date), 'MMM d, yyyy h:mm a') : '';

  const statusConfig = {
    new: {
      color: 'bg-emerald-500',
      label: 'New',
      icon: AlertCircle,
      textColor: 'text-emerald-700 dark:text-emerald-400'
    },
    assigned: {
      color: 'bg-blue-500',
      label: 'Assigned',
      icon: UserCheck,
      textColor: 'text-blue-700 dark:text-blue-400'
    },
    in_progress: {
      color: 'bg-amber-500',
      label: 'In Progress',
      icon: Clock,
      textColor: 'text-amber-700 dark:text-amber-400'
    },
    resolved: {
      color: 'bg-gray-400',
      label: 'Resolved',
      icon: CheckCircle2,
      textColor: 'text-gray-700 dark:text-gray-400'
    },
    wont_fix: {
      color: 'bg-red-500',
      label: 'Won\'t Fix',
      icon: X,
      textColor: 'text-red-700 dark:text-red-400'
    }
  };

  const currentStatus = statusConfig[feedback?.status] || statusConfig.new;

  const priorityConfig = {
    low: { color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', label: 'Low' },
    medium: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', label: 'Medium' },
    high: { color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400', label: 'High' },
    critical: { color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', label: 'Critical' }
  };

  const priority = priorityConfig[feedback?.priority] || priorityConfig.low;

  const handleStatusUpdate = useCallback((updates) => {
    if (updates.status && updates.status !== feedback?.status) {
      sendStatusUpdateEmail(userData.email, feedback?.comment, feedback?.status, updates.status, updates.assignedTo);
    }
    onStatusUpdate(updates);
    toast({
      title: 'Feedback updated',
      description: 'Changes saved successfully',
      duration: 2000,
    });
  }, [onStatusUpdate, toast, feedback, userData]);

  const handleEmailUser = useCallback(() => {
    if (userData.email) {
      window.open(`mailto:${userData.email}?subject=Feedback Update&body=Hi ${name},%0A%0ARegarding your feedback: ${feedback?.comment}`);
    }
  }, [userData, feedback, name]);

  const handleSaveNote = useCallback(() => {
    handleStatusUpdate({ ...feedback, internalNote });
    setIsEditingNote(false);
  }, [feedback, internalNote, handleStatusUpdate]);

  if (!feedback) {
    return (
      <div className="h-full flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 rounded-xl bg-muted flex items-center justify-center mx-auto">
            <MessageSquare className="h-6 w-6 text-muted-foreground" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-foreground mb-1">No Feedback Selected</h3>
            <p className="text-sm text-muted-foreground">Choose a feedback item to view details</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: isMobile ? 0 : 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: isMobile ? 0 : 20 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className={cn(
        "h-full flex flex-col bg-background",
        isMobile && "fixed inset-0 z-50"
      )}
    >
      {/* Mobile Header */}
      {isMobile && (
        <div className="flex items-center gap-3 p-4 border-b border-border/50 bg-background/95 backdrop-blur-sm sticky top-0 z-10">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="gap-2 h-10 px-3 rounded-xl"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="text-sm font-medium">Back</span>
          </Button>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-medium truncate">Feedback Details</h2>
            <p className="text-xs text-muted-foreground truncate">{name}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant="secondary"
              className={cn(
                "text-xs px-2 py-1",
                priority.color
              )}
            >
              {priority.label}
            </Badge>
            <div className={cn('w-2 h-2 rounded-full', currentStatus.color)} />
          </div>
        </div>
      )}

      {/* Desktop Header */}
      {!isMobile && (
        <div className="flex items-center justify-between p-6 border-b border-border/50">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl">{feedback.emoji}</span>
              <Badge className={cn(
                'text-xs font-medium',
                currentStatus.color
              )}>
                {currentStatus.label}
              </Badge>
              {feedback.priority && (
                <Badge
                  variant="secondary"
                  className={cn(
                    "text-xs",
                    priority.color
                  )}
                >
                  {priority.label}
                </Badge>
              )}
            </div>
            <h2 className="text-lg font-semibold text-foreground truncate mb-1">
              {feedback.comment.split('\n')[0].slice(0, 60)}
              {feedback.comment.split('\n')[0].length > 60 && '...'}
            </h2>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span>{date}</span>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className={cn(
          "space-y-4",
          isMobile ? "p-4" : "p-6"
        )}>
          {/* User Info Card */}
          <div className="bg-card rounded-xl p-4 border border-border/50">
            <div className="flex items-center gap-3">
              <Avatar className={cn(
                "flex-shrink-0",
                isMobile ? "h-12 w-12" : "h-10 w-10"
              )}>
                {userData.photoURL ? (
                  <img src={userData.photoURL} alt={name} className="w-full h-full rounded-full object-cover" />
                ) : (
                  <AvatarFallback className={cn(
                    "font-medium bg-muted text-muted-foreground",
                    isMobile ? "text-sm" : "text-sm"
                  )}>
                    {initials}
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="flex-1 min-w-0">
                <h3 className={cn(
                  "font-medium text-foreground",
                  isMobile ? "text-base" : "text-sm"
                )}>
                  {name}
                </h3>
                <p className={cn(
                  "text-muted-foreground truncate",
                  isMobile ? "text-sm" : "text-xs"
                )}>
                  {userData.email}
                </p>
              </div>
              <Button
                variant="outline"
                size={isMobile ? "default" : "sm"}
                onClick={handleEmailUser}
                className="gap-2"
              >
                <Mail className="h-4 w-4" />
                {!isMobile && "Email"}
              </Button>
            </div>
          </div>

          {/* Feedback Content Card */}
          <div className="space-y-3">
            <h4 className={cn(
              "font-medium text-foreground",
              isMobile ? "text-base" : "text-sm"
            )}>
              Feedback
            </h4>
            <div className="bg-card rounded-xl p-4 border border-border/50">
              <p className={cn(
                "text-foreground leading-relaxed whitespace-pre-line",
                isMobile ? "text-base" : "text-sm"
              )}>
                {feedback.comment}
              </p>
            </div>
          </div>

          {/* Internal Notes Card */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className={cn(
                "font-medium text-foreground",
                isMobile ? "text-base" : "text-sm"
              )}>
                Internal Notes
              </h4>
              {!isEditingNote && (
                <Button
                  variant="ghost"
                  size={isMobile ? "default" : "sm"}
                  onClick={() => setIsEditingNote(true)}
                  className="gap-1"
                >
                  <Edit3 className="h-4 w-4" />
                  {!isMobile && "Edit"}
                </Button>
              )}
            </div>

            {isEditingNote ? (
              <div className="bg-card rounded-xl p-4 border border-border/50 space-y-3">
                <Textarea
                  placeholder="Add notes for the team..."
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                  className={cn(
                    "min-h-[100px] resize-none",
                    isMobile && "min-h-[120px]"
                  )}
                />
                <div className="flex gap-2">
                  <Button
                    onClick={handleSaveNote}
                    size={isMobile ? "default" : "sm"}
                    className="gap-1"
                  >
                    <Save className="h-4 w-4" />
                    Save
                  </Button>
                  <Button
                    variant="outline"
                    size={isMobile ? "default" : "sm"}
                    onClick={() => {
                      setInternalNote(feedback.internalNote || '');
                      setIsEditingNote(false);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="bg-card rounded-xl p-4 border border-border/50 min-h-[60px]">
                {internalNote ? (
                  <p className={cn(
                    "text-foreground leading-relaxed whitespace-pre-line",
                    isMobile ? "text-base" : "text-sm"
                  )}>
                    {internalNote}
                  </p>
                ) : (
                  <p className={cn(
                    "text-muted-foreground italic",
                    isMobile ? "text-base" : "text-sm"
                  )}>
                    No internal notes
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className={cn(
        "border-t border-border/50 bg-muted/30",
        isMobile ? "p-4" : "p-6"
      )}>
        <div className={cn(
          "flex items-center gap-4",
          isMobile && "flex-col gap-3"
        )}>
          <div className={cn(
            "flex items-center gap-2",
            isMobile && "w-full"
          )}>
            <span className={cn(
              "font-medium text-muted-foreground",
              isMobile ? "text-sm" : "text-xs"
            )}>
              Status:
            </span>
            <Select
              value={feedback.status}
              onValueChange={status => handleStatusUpdate({ ...feedback, status })}
            >
              <SelectTrigger className={cn(
                "text-xs",
                isMobile ? "w-full h-10" : "w-32 h-8"
              )}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="wont_fix">Won't Fix</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className={cn(
            "flex items-center gap-2",
            isMobile && "w-full"
          )}>
            <span className={cn(
              "font-medium text-muted-foreground",
              isMobile ? "text-sm" : "text-xs"
            )}>
              Assign:
            </span>
            <Select
              value={feedback.assignedTo || 'unassigned'}
              onValueChange={value => handleStatusUpdate({ ...feedback, assignedTo: value === 'unassigned' ? null : value })}
            >
              <SelectTrigger className={cn(
                "text-xs",
                isMobile ? "w-full h-10" : "w-36 h-8"
              )}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                <SelectItem value="admin_dilhan">Dilhan</SelectItem>
                <SelectItem value="admin_support">Support Team</SelectItem>
                <SelectItem value="admin_developer">Dev Team</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default FeedbackDetailView;